import React, { useState, useEffect } from 'react';
import { useLocation, Link, useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Home, RefreshCw, ChevronDown, ChevronUp, Loader2, BrainCircuit } from 'lucide-react';
import {
    CertExamDefinition,
    CertExamResult,
    CertQuestionResult,
    CertQuestion,
    CertQuestionType,
    LearningMode,
    ProgrammingLanguage
} from '@unity-learning/shared';
import {
    QuestionMatrix,
    QuestionMCQSingle,
    QuestionMCQMulti,
    QuestionMatchingDrag,
    QuestionOrdering,
    QuestionCodeDropdown,
    QuestionCodeInput,
    QuestionCodeConstruction,
    QuestionBoolean,
    QuestionPrompt
} from '../components/questions';
import { useUser } from '../hooks/useUser';
import { GenerationFailedNotice } from '../components/GenerationFailedNotice';
import { generateExercise, getCertificationAttempt, getCertificationExam, isApiError } from '../services/api';

export const CertificationResultsPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const location = useLocation();
    const { attemptId } = useParams<{ attemptId: string }>();
    const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
    const [generatingChallengeId, setGeneratingChallengeId] = useState<string | null>(null);
    // Échec du défi : affiché sous son bouton, le corrigé reste à l'écran.
    const [challengeFailure, setChallengeFailure] = useState<{ questionId: string; message: string } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fetchedResult, setFetchedResult] = useState<CertExamResult | null>(null);
    const [fetchedExam, setFetchedExam] = useState<CertExamDefinition | null>(null);
    const [embeddedQuestions, setEmbeddedQuestions] = useState<CertQuestion[]>([]);  // For AI training attempts

    // Get result from navigation state (when coming from exam submission)
    const state = location.state as { result: CertExamResult; exam: CertExamDefinition } | null;

    // Fetch from API if accessed via URL with attemptId
    useEffect(() => {
        if (!state && attemptId) {
            const fetchAttempt = async () => {
                setIsLoading(true);
                setError(null);
                try {
                    // Fetch attempt details
                    let attempt;
                    try {
                        attempt = await getCertificationAttempt(attemptId);
                    } catch (loadError) {
                        throw new Error(isApiError(loadError) && !loadError.isNetworkError && loadError.message
                            ? loadError.message
                            : 'Impossible de charger la tentative');
                    }

                    // Les questions voyagent avec la copie (`exam`), même quand le formateur a refermé
                    // l'examen blanc ; l'ancien rechargement par GET /exam reste en secours.
                    const examId = attempt.exam_id;
                    let examDefinition: CertExamDefinition | null = attempt.exam ?? null;
                    if (!examDefinition && !examId.startsWith('ai_training_')) {
                        try {
                            examDefinition = await getCertificationExam(examId);
                        } catch {
                            console.warn('Could not load exam definition, using attempt data only');
                        }
                    }

                    // Transform attempt data to match CertExamResult
                    const resultData: CertExamResult = {
                        attemptId: attempt.id,
                        score: attempt.score,
                        maxScore: attempt.max_score,
                        passed: attempt.passed === 1 || attempt.passed === true,
                        passingThreshold: examDefinition?.passing_score_percent ?? 70,
                        questionResults: attempt.questionResults
                            ?? (typeof attempt.question_results === 'string'
                                ? JSON.parse(attempt.question_results)
                                : (attempt.question_results || []))
                    };

                    // Check if answers contains embedded questions (AI training mode)
                    // Note: answers may already be parsed by the backend
                    const answers = typeof attempt.answers === 'string'
                        ? JSON.parse(attempt.answers)
                        : (attempt.answers || {});
                    if (answers.questions && Array.isArray(answers.questions)) {
                        setEmbeddedQuestions(answers.questions);
                    }

                    setFetchedResult(resultData);
                    setFetchedExam(examDefinition);
                } catch (err) {
                    console.error('Error loading attempt:', err);
                    setError((err as Error).message);
                } finally {
                    setIsLoading(false);
                }
            };

            fetchAttempt();
        }
    }, [attemptId, state]);

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <div className="text-gray-600 dark:text-gray-400">Chargement des résultats…</div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-600 dark:text-red-400 mb-4">{error}</div>
                    <Link
                        to="/progression"
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Retour à la progression
                    </Link>
                </div>
            </div>
        );
    }

    // Use either state data or fetched data
    const result = state?.result || fetchedResult;
    const exam = state?.exam || fetchedExam;

    // For AI training mode, we may not have an exam but we have embedded questions
    const hasQuestions = (exam?.questions && exam.questions.length > 0) || embeddedQuestions.length > 0;

    if (!result || !hasQuestions) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="text-gray-600 dark:text-gray-400 mb-4">
                        Aucun résultat d'examen trouvé.
                    </div>
                    <Link
                        to="/certification"
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Passer un nouvel examen
                    </Link>
                </div>
            </div>
        );
    }

    const percentage = Math.round((result.score / result.maxScore) * 100);

    // Get question by ID - search in exam questions or embedded questions (AI training)
    const getQuestion = (questionId: string): CertQuestion | undefined => {
        // First try to find in exam questions
        if (exam?.questions) {
            const found = exam.questions.find(q => q.id === questionId);
            if (found) return found;
        }
        // Then try embedded questions (for AI training mode)
        return embeddedQuestions.find(q => q.id === questionId);
    };

    // Generate a custom challenge based on the question
    const handleChallengeMe = async (question: CertQuestion, userAnswer: any) => {
        if (!user) return;
        setGeneratingChallengeId(question.id);
        setChallengeFailure(null);

        // B7: the precise topic lives in the tags (AI Training stores its blueprint topic there,
        // static exams carry identifiers like "GetComponent" or "Coroutines"). There is NO
        // question.topic field — the old prompt printed "Topic: undefined".
        const topic = (question.tags || []).filter(t => t && t.trim()).join(', ');

        const customPrompt = `Create a Unity C# coding exercise based on the certification question below${topic ? `. The exercise MUST target EXACTLY this topic: "${topic}"` : ''}.

QUESTION:
"${question.prompt}"

CONTENT (Code/Scenario):
${JSON.stringify(question.content, null, 2)}

STUDENT'S EXAM ANSWER:
${JSON.stringify(userAnswer)}

TASK:
1. Analyze the CONTENT to identify the specific API call, syntax, or pattern being tested by this question.
2. Create a coding challenge where the expected solution requires using that EXACT API/Syntax. Stay strictly on the topic of the question — do NOT drift to a neighbouring Unity topic.
3. CONSTRAINTS: Explicitly forbid shortcuts if the original question tested the verbose method: the exercise must require the exact form the question tested, not an equivalent shorthand.
4. The goal is to verify the student understands the specific mechanism shown in the question, so strict adherence to the pattern is required.`;

        try {
            const created = await generateExercise({
                userId: user.id,
                // Certification exists only for Unity, so the challenged topic is a Unity
                // topic: always generate a Unity C# exercise, even if the user is currently
                // in godot/pyjs mode (a "Rigidbody" drill in GDScript would make no sense).
                learningMode: LearningMode.UNITY_CSHARP,
                programmingLanguage: ProgrammingLanguage.CSHARP,
                customPrompt,
                // Focused validation target for the B5 coverage check (see gemini.ts)
                ...(topic ? { customPromptTopic: topic } : {}),
                adaptToProgress: false // Focused drill
            });
            navigate(`/exercise/${created.id}`);
        } catch (err) {
            setChallengeFailure({
                questionId: question.id,
                message: isApiError(err) && !err.isNetworkError
                    ? err.message || 'Impossible de générer le défi'
                    : 'Erreur lors de la génération du défi',
            });
        } finally {
            setGeneratingChallengeId(null);
        }
    };

    // Render a question component in review mode (disabled, showing a specific value)
    // `expected` : la bonne réponse, pour colorer chaque case de saisie (questions à trous)
    const renderQuestionReview = (question: CertQuestion, value: any, label: string, isCorrect: boolean, expected?: any) => {
        const commonProps = {
            disabled: true,
            onChange: () => { },
        };

        const borderClass = isCorrect
            ? 'border-2 border-green-400 dark:border-green-600 rounded-lg p-4 bg-green-50/50 dark:bg-green-900/10'
            : 'border-2 border-red-400 dark:border-red-600 rounded-lg p-4 bg-red-50/50 dark:bg-red-900/10';

        const labelClass = isCorrect
            ? 'text-green-700 dark:text-green-400'
            : 'text-red-700 dark:text-red-400';

        const renderComponent = () => {
            switch (question.type) {
                case CertQuestionType.TYPE_MATRIX:
                    return (
                        <QuestionMatrix
                            question={question}
                            value={value || {}}
                            reviewAgainst={expected || {}}
                            {...commonProps}
                        />
                    );

                case CertQuestionType.TYPE_MCQ_SINGLE:
                    return (
                        <QuestionMCQSingle
                            question={question}
                            value={value || null}
                            {...commonProps}
                        />
                    );

                case CertQuestionType.TYPE_MATCHING_DRAG:
                    return (
                        <QuestionMatchingDrag
                            question={question}
                            value={value || {}}
                            {...commonProps}
                        />
                    );

                case CertQuestionType.TYPE_ORDERING:
                    return (
                        <QuestionOrdering
                            question={question}
                            value={value || []}
                            {...commonProps}
                        />
                    );

                case CertQuestionType.TYPE_CODE_DROPDOWN:
                    return (
                        <QuestionCodeDropdown
                            question={question}
                            value={value || {}}
                            reviewAgainst={expected || {}}
                            {...commonProps}
                        />
                    );

                case CertQuestionType.TYPE_CODE_INPUT:
                    return (
                        <QuestionCodeInput
                            question={question}
                            value={value || {}}
                            reviewAgainst={expected || {}}
                            {...commonProps}
                        />
                    );

                case CertQuestionType.TYPE_CODE_CONSTRUCTION:
                    return (
                        <QuestionCodeConstruction
                            question={question}
                            value={value || {}}
                            reviewAgainst={expected || {}}
                            {...commonProps}
                        />
                    );

                case CertQuestionType.TYPE_MCQ_MULTI:
                    return (
                        <QuestionMCQMulti
                            question={question}
                            value={value || []}
                            {...commonProps}
                        />
                    );

                case CertQuestionType.TYPE_BOOLEAN:
                    return (
                        <QuestionBoolean
                            question={question}
                            value={value}
                            {...commonProps}
                        />
                    );

                default:
                    return <div className="text-gray-500">Type de question non pris en charge : {question.type}</div>;
            }
        };

        return (
            <div className={borderClass}>
                <div className={`text-sm font-semibold mb-3 ${labelClass}`}>
                    {label}
                </div>
                {renderComponent()}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="w-full px-6 lg:px-12">
                {/* Score Card */}
                <div className={`
          rounded-xl shadow-lg p-8 mb-8 max-w-2xl mx-auto
          ${result.passed
                        ? 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 border border-green-200 dark:border-green-700'
                        : 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30 border border-red-200 dark:border-red-700'
                    }
        `}>
                    <div className="text-center">
                        {/* Pass/Fail Icon */}
                        {result.passed ? (
                            <CheckCircle className="w-16 h-16 mx-auto text-green-600 dark:text-green-400 mb-4" />
                        ) : (
                            <XCircle className="w-16 h-16 mx-auto text-red-600 dark:text-red-400 mb-4" />
                        )}

                        {/* Pass/Fail Banner */}
                        <div className={`
              inline-block px-6 py-2 rounded-full font-bold text-lg mb-4
              ${result.passed ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}
            `}>
                            {result.passed ? '🎉 Réussi !' : 'Non réussi'}
                        </div>

                        {/* Score */}
                        <div className="text-5xl font-bold text-gray-900 dark:text-white mb-2">
                            {result.score} / {result.maxScore}
                        </div>
                        <div className="text-xl text-gray-600 dark:text-gray-400">
                            {percentage} % (seuil de réussite : {result.passingThreshold} %)
                        </div>
                    </div>
                </div>

                {/* Question Breakdown */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                        Détail des questions
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                        Cliquez sur une question pour comparer votre réponse à la bonne réponse.
                    </p>

                    <div className="space-y-3">
                        {result.questionResults.map((qr: CertQuestionResult, idx: number) => {
                            const question = getQuestion(qr.questionId);
                            const isExpanded = expandedQuestion === qr.questionId;

                            return (
                                <div
                                    key={qr.questionId}
                                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                                >
                                    {/* Question Header */}
                                    <button
                                        onClick={() => setExpandedQuestion(isExpanded ? null : qr.questionId)}
                                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-4">
                                            {/* Status Icon */}
                                            <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold
                        ${qr.isCorrect
                                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                                    : qr.pointsEarned > 0
                                                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                                                        : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                                                }
                      `}>
                                                {qr.isCorrect ? '✓' : qr.pointsEarned > 0 ? '~' : '✗'}
                                            </div>

                                            {/* Question Number & Title */}
                                            <div className="text-left">
                                                <div className="font-semibold text-gray-900 dark:text-white">
                                                    Question {idx + 1}
                                                </div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                    {question?.tags?.join(', ') || question?.type}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            {/* Points */}
                                            <div className={`
                        font-bold text-lg
                        ${qr.isCorrect
                                                    ? 'text-green-600 dark:text-green-400'
                                                    : qr.pointsEarned > 0
                                                        ? 'text-yellow-600 dark:text-yellow-400'
                                                        : 'text-red-600 dark:text-red-400'
                                                }
                      `}>
                                                +{qr.pointsEarned} pts
                                            </div>

                                            {/* Expand Icon */}
                                            {isExpanded ? (
                                                <ChevronUp className="w-6 h-6 text-gray-400" />
                                            ) : (
                                                <ChevronDown className="w-6 h-6 text-gray-400" />
                                            )}
                                        </div>
                                    </button>

                                    {/* Expanded Details - Show question with user answer + correct answer */}
                                    {isExpanded && question && (
                                        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900/50">
                                            {/* Question Prompt */}
                                            <div className="mb-6">
                                                <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                                                    Énoncé :
                                                </div>
                                                <QuestionPrompt text={question.prompt} />
                                            </div>

                                            {/* Side by side or stacked comparison */}
                                            <div className="space-y-6">
                                                {/* Your Answer */}
                                                {renderQuestionReview(
                                                    question,
                                                    qr.userAnswer,
                                                    '📝 Votre réponse',
                                                    qr.isCorrect,
                                                    // Toutes les écritures acceptées, pour colorer la copie comme la notation
                                                    qr.acceptedAnswers ?? qr.correctAnswer
                                                )}

                                                {/* Correct Answer */}
                                                {!qr.isCorrect && renderQuestionReview(
                                                    question,
                                                    qr.correctAnswer,
                                                    '✅ Bonne réponse',
                                                    true,
                                                    qr.correctAnswer
                                                )}
                                            </div>

                                            {qr.explanation && (
                                                <div className="mt-6 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
                                                    <div className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">
                                                        💡 À savoir pour l'examen
                                                    </div>
                                                    <p className="text-sm text-gray-800 dark:text-gray-200">{qr.explanation}</p>
                                                </div>
                                            )}

                                            {/* Challenge Me Button */}
                                            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-800 flex justify-end">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleChallengeMe(question, qr.userAnswer);
                                                    }}
                                                    disabled={generatingChallengeId === question.id}
                                                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 shadow-md hover:shadow-lg"
                                                >
                                                    {generatingChallengeId === question.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <BrainCircuit className="w-4 h-4" />
                                                    )}
                                                    Me défier sur ce point
                                                </button>
                                            </div>
                                            {challengeFailure?.questionId === question.id && (
                                                <GenerationFailedNotice
                                                    message={challengeFailure.message}
                                                    onRetry={() => handleChallengeMe(question, qr.userAnswer)}
                                                    onDismiss={() => setChallengeFailure(null)}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 justify-center">
                    <Link
                        to="/"
                        className="flex items-center gap-2 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                        <Home className="w-5 h-5" />
                        Retour à l'accueil
                    </Link>
                    <Link
                        to="/certification"
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <RefreshCw className="w-5 h-5" />
                        Passer un autre examen
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CertificationResultsPage;
